import { useContext } from 'react';
import { AppContext } from 'contexts';
import { Page } from 'components';
import { useTranslation } from 'react-i18next';
import SupportContactCard from './_ContactCard/SupportContactCard';
import { Box, Paper, TextButton, Typography, Icon, visuallyHidden, styled } from 'bonfire-kindling';
import theme from 'constants/theme';

const StyledPageContainer = styled(Box)(({ theme }) => ({
  padding: `${theme.spacing(6)} ${theme.spacing(3)}`,
  display: 'flex',
  minHeight: '100%',
  flexDirection: 'column',
  justifyContent: 'space-around',
  alignItems: 'center',
  alignContent: 'center',
  margin: '0 auto',
  maxWidth: theme.breakpoints.values.md,
}));

const StyledHelpCenterPaper = styled(Paper)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  boxShadow: '0px 0px 8px #e1e4e8',
  width: '100%',
  padding: `${theme.spacing(4)} ${theme.spacing(3)}`,
}));

const StyledSupportButton = styled(TextButton)(({ theme }) => ({
  backgroundColor: theme.palette.support.main,
  color: 'white',
  border: 0,
  '&:hover': {
    backgroundColor: theme.palette.support.dark,
  },
}));

const Support = () => {
  const { firstName } = useContext(AppContext);
  const { t } = useTranslation();

  return (
    <Page isTinted>
      <StyledPageContainer>
        <Typography style={visuallyHidden} component="h1">
          {t('support.title')}
        </Typography>
        <Typography variant="h1" component="h2" sx={{ textAlign: 'center', mb: 4 }}>
          {t('support.can_we_help', { name: firstName })}
        </Typography>

        <StyledHelpCenterPaper>
          <Box
            sx={{
              display: 'flex',
              textAlign: { xs: 'center', sm: 'left' },
              flexDirection: { xs: 'column', sm: 'row' },
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Box sx={{ mb: { xs: 3, sm: 0 } }}>
              <Typography variant="h4" paragraph component="h3">
                {t('support.common_questions')}
              </Typography>
              <Typography variant="body2">{t('support.find_how')}</Typography>
            </Box>

            <StyledSupportButton
              label={t('support.see_articles')}
              component="a"
              href="https://vendorsupport.gobonfire.com/hc/en-us"
              size="medium"
            />
          </Box>
        </StyledHelpCenterPaper>

        <Box textAlign="center" my={5}>
          <Typography variant="h2" component="h3" gutterBottom>
            {t('support.need_help')}
          </Typography>
          <Typography paragraph>{t('support.reach_out')}</Typography>
          <Typography style={{ fontStyle: 'italic', fontSize: '1.125rem' }}>
            {t('support.hours')}
          </Typography>
        </Box>

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            flexDirection: { xs: 'column', sm: 'row' },
            '& > * + *': {
              ml: { xs: 0, sm: 4 },
              mt: { xs: 4, sm: 0 },
            },
          }}
        >
          <SupportContactCard
            actionCall={t('support.email_us')}
            icon={
              <Icon
                iconName="envelope"
                style={{ fontSize: '2rem' }}
                color={theme.palette.support.main}
              />
            }
          >
            <a style={{ fontWeight: 'bold' }} href="mailto: Support@gobonfire.com">
              Support@GoBonfire.com
            </a>
          </SupportContactCard>
          <SupportContactCard
            actionCall={t('support.call_us')}
            icon={
              <Icon
                iconName="phone-alt"
                style={{ fontSize: '2rem' }}
                color={theme.palette.support.main}
              />
            }
          >
            <Typography>1-800-354-8010</Typography>
            <Typography>Ex.2</Typography>
          </SupportContactCard>
        </Box>
      </StyledPageContainer>
    </Page>
  );
};

export default Support;
